import React, { FC, useState, useRef, useId, useEffect } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import {
  Button,
  Eyebrow,
  EyebrowEnum,
  Heading,
  headingType,
  Icon,
  size,
  Image,
  LogoContainer,
  tags,
  LogoContainerBackgroundColor,
  useCheckBreakpoint,
  Types,
} from '@marriott/mi-ui-library';
import { ProductDetailsCardProps } from './ProductDetailsCard.types';
import {
  StyledProductDetailsCard,
  StyledEyebrow,
  StyledTitleRow,
  StyledTags,
  TagsWrapper,
  StyledBadge,
} from './ProductDetailsCard.styles';
import { getClickTrackValue } from '../../utils/getClickTrackValue';

const ProductDetailsCardComp: FC<ProductDetailsCardProps> = ({
  className,
  componentId,
  styleclass,
  cardVariation,
  cardUrl,
  openInaNewTab,
  icon,
  eyebrow,
  badgeIcon,
  badgeText,
  title,
  tagListItems,
  subTitle,
  description,
  brandLogoIcon,
  fileReferenceImage,
  imageAltText,
  dynamicMedia,
  dynamicMediaTall,
  dynamicMediaWide,
  trackingProperties,
  breakPointKey = 'viewportXl', // default value for desktop is greater than 1200
}) => {
  const subTitleRef = useRef<HTMLDivElement>(null);
  const [subTitleTabIndex, setSubTitleTabIndex] = useState<number>(0);
  const [isExpanded, setIsExpanded] = useState(false);
  // Generate unique ids to prevent screenreaders from reading only the first card
  const id = useId();
  const renderedTagListItems = badgeText ? tagListItems?.slice(0, 1) : tagListItems;
  const renderTags = (tagListItems: { tagLabel: string }[] | undefined) =>
    tagListItems &&
    tagListItems.length > 0 && (
      <StyledTags id={`${id}tags`} role="list">
        {renderedTagListItems?.map(tag => (
          <li key={tag.tagLabel} role="listitem" {...(!isDesktopViewPort && { tabIndex: 0 })}>
            {tag.tagLabel}
          </li>
        ))}
      </StyledTags>
    );

  const isDesktopViewPort = useCheckBreakpoint(breakPointKey); //is the view port greater than 1200px or 992px
  let imageRendition = dynamicMedia;
  if (cardVariation === 'tall' && dynamicMediaTall && isDesktopViewPort) {
    imageRendition = dynamicMediaTall;
  } else if (cardVariation === 'wide' && dynamicMediaWide && isDesktopViewPort) {
    imageRendition = dynamicMediaWide;
  }
  const brand = brandLogoIcon?.split('/').pop();
  const brandLogoIconPath = `https://www.dev-cdn.marriott.com/mi-assets/mi-global/brand-logos/${brand}.svg`;
  const iconClass = (iconClass: string) => iconClass?.split(':').pop();

  useEffect(() => {
    /*
      For mobile and tablet accesibility, Setting the focus to subtitle on the click of expand button
      Since the subitle appears earlier in the DOM, So focus is set manually via ref and tabIndex -1
    */
    if (isExpanded) {
      if (subTitleRef.current) {
        subTitleRef.current?.focus();
      }
    }
  }, [isExpanded, subTitleTabIndex]);

  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    setSubTitleTabIndex(isExpanded ? -1 : 0);
  };

  const tagsId = tagListItems && tagListItems?.length > 0 ? `${id}tags` : '';
  const brandId = brandLogoIcon ? `${id}logo` : '';

  return (
    <StyledProductDetailsCard
      $isExpanded={isExpanded}
      cardVariation={cardVariation}
      breakPointKey={breakPointKey}
      className={clsx(`pdc-${cardVariation} inverse skeleton-loader`, className, styleclass)}
      data-testid="homepage-ProductDetailsCard"
      data-component-name="o-homepage-ProductDetailsCard"
      id={componentId}
    >
      <Button
        isLink
        href={cardUrl}
        target={openInaNewTab ? '_blank' : '_self'}
        className={clsx('pdc-card', { 'is-expanded': !isDesktopViewPort && isExpanded })}
        rel={openInaNewTab ? 'noopener noreferrer' : undefined}
        trackingProperties={trackingProperties}
        {...(isDesktopViewPort && {
          ariaDescribedBy: `${id}eyebrow ${id}title ${
            badgeText ? `${id}badge` : ''
          } ${tagsId} ${id}subtitle ${id}description ${brandId}`,
        })}
        custom_click_track_value={getClickTrackValue({
          trackingProperties,
          url: cardUrl,
          fallbacks: { position: '', description: '' },
        })}
      >
        <Image
          altText={imageAltText}
          renditions={imageRendition?.renditions}
          defaultImageURL={fileReferenceImage}
          customClass="pdc-image"
        />
        <div className="pdc-content">
          <div className="pdc-content-top">
            {eyebrow && (
              <StyledEyebrow
                id={`${id}eyebrow`}
                {...(!isDesktopViewPort && { tabIndex: 0, 'aria-label': eyebrow })}
                role="note"
              >
                <Icon iconClass={clsx(icon && iconClass(icon), 'icon-xs')} />{' '}
                <Eyebrow text={eyebrow} variation={EyebrowEnum.Overlay} />{' '}
              </StyledEyebrow>
            )}
            {title && (
              <StyledTitleRow
                id={`${id}title`}
                {...(!isDesktopViewPort && { tabIndex: 0, 'aria-label': title })}
                role="note"
              >
                <Heading
                  variation={headingType.subtitle}
                  titleText={title}
                  fontSize={size.extraLarge}
                  element={tags.paragraph}
                  customClass="m-0 m-ellipsis-2lines"
                />
                <Icon iconClass={clsx('icon-arrow-right', 'icon-l')} />
              </StyledTitleRow>
            )}
            <TagsWrapper>
              {badgeText && (
                <StyledBadge
                  {...(!isDesktopViewPort && { tabIndex: 0 })}
                  badgeText={badgeText}
                  badgeIcon={badgeIcon && iconClass(badgeIcon)}
                  badgeVariation={Types.BadgeVariation.Overlay}
                  id={`${id}badge`}
                  role="note"
                />
              )}
              {renderTags(tagListItems)}
            </TagsWrapper>
            {subTitle && (
              <div
                id={`${id}subtitle`}
                ref={subTitleRef}
                {...(!isDesktopViewPort && { tabIndex: subTitleTabIndex, role: 'note' })}
                className="pdc-hovercontent"
              >
                <Heading
                  variation={headingType.body}
                  titleText={subTitle}
                  fontSize={size.small}
                  element={tags.paragraph}
                  customClass="m-0"
                />
              </div>
            )}
            {description && (
              <p
                id={`${id}description`}
                {...(!isDesktopViewPort && { tabIndex: 0, role: 'note' })}
                aria-label={description}
                className="t-font-s pdc-hovercontent m-0"
              >
                {description}
              </p>
            )}
          </div>
          <div className="pdc-content-bottom">
            {brandLogoIcon && (
              <div
                className="pdc-brand-logo"
                id={`${id}logo`}
                {...(!isDesktopViewPort && { tabIndex: 0, role: 'note' })}
              >
                <LogoContainer
                  ariaRole="note"
                  alignment="Centered"
                  brand={brand || ''}
                  aspectRatio="3:2"
                  width={78}
                  height={52}
                  logopath={brandLogoIconPath}
                  color="background-aware"
                  backgroundColor={LogoContainerBackgroundColor.Inverse}
                />
              </div>
            )}
            <button
              type="button"
              className={`pdc-mobile-toggle ${isExpanded ? 'is-expanded' : ''}`}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Close additional information' : 'Expand to see more information'}
              onClick={toggleExpand}
              onMouseDown={e => {
                e.stopPropagation();
              }}
              onTouchStart={e => {
                e.stopPropagation();
              }}
            >
              <span className={`icon-info ${isExpanded ? 'd-none' : 'd-flex'}`}>
                <svg width="3" height="16" viewBox="0 0 3 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="3" height="9" transform="translate(0 6.5)" fill="#1C1C1C" />
                  <rect width="3" height="3" transform="translate(0 0.5)" fill="#1C1C1C" />
                </svg>
              </span>
              <span className={`icon-clear ${isExpanded ? 'd-flex' : 'd-none'}`} />
            </button>
          </div>
        </div>
      </Button>
    </StyledProductDetailsCard>
  );
};

export const ProductDetailsCard = (props: ProductDetailsCardProps) => {
  return (
    <EditableComponent
      config={{
        emptyLabel: 'Product Details Card',
        isEmpty: () => false,
        resourceType: 'mi-aem-homepage-spa/components/content/productdetailscard',
      }}
      {...props}
    >
      <ProductDetailsCardComp {...props} />
    </EditableComponent>
  );
};
